import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { OrchestratorsContext } from "@nju/data-access-portal-orchestrator";
import { ClientOrderInfoBox } from "@nju/feature-sales-client";
import { redirectToOrchestratorState } from "@nju/feature-sales-orchestrator";
import { salesRoutes } from "@nju/routes";
import {
  Alert,
  BlockContent,
  BlockText,
  Button,
  CircleLoader,
  GeneralErrorNotification,
  Modal,
  Separator,
  useOverlay,
} from "@nju/ui";
import { statusCustomerMachine } from "./customerStatus.machine";
import { CustomerStatusBanner } from "./CustomerStatusBanner/CustomerStatusBanner";
import { CustomerStatusDetails } from "./CustomerStatusDetails/CustomerStatusDetails";
import { getFtthOrchestratorPhase } from "./partials/getFtthOrchestratorPhase";
import { getMobileOrchestratorPhase } from "./partials/getMobileOrchestratorPhase";
import { IStatusScope } from "./partials/types";

interface ICustomerStatusPageProps {
  statusVariant?: IStatusScope;
}

export function CustomerStatusPage({
  statusVariant = "banner",
}: ICustomerStatusPageProps) {
  const modal = useOverlay();
  const [current, send] = useMachine(statusCustomerMachine, {
    actions: {
      onNewFtthOrderSuccess: () => {
        if (context.ftthOrchestrator) {
          if (context.ftthOrchestrator.state === "START") {
            if (context.isAddressInFtthRange) {
              return router.push(salesRoutes.wybierzOferte.get());
            }
            //We need to check if context.isAddressInFtthRange === false because this key can have 3 values - true, false or undefined.
            if (context.isAddressInFtthRange === false) {
              return router.push(
                salesRoutes.sprawdzDostepnosc.uslugaNiedostepna.get(),
              );
            }
            router.push(salesRoutes.sprawdzDostepnosc.index.get());
          } else {
            return redirectToOrchestratorState({
              state: context.ftthOrchestrator.state,
              mode: "ftth",
            });
          }
        }

        return router.push(salesRoutes.sprawdzDostepnosc.index.get());
      },
      onNewMultiOrderSuccess: () => {
        if (context.multiOrderOrchestrator) {
          if (context.multiOrderOrchestrator.state === "ITEM_SELECTION") {
            return redirectToOrchestratorState({
              state: context.multiOrderOrchestrator.state,
              mode: "mobile",
            });
          }

          if (context.multiOrderOrchestrator.state === "START") {
            return router.push(salesRoutes.wybierak.get({ mode: "mobile" }));
          } else {
            return redirectToOrchestratorState({
              state: context.multiOrderOrchestrator.state,
              mode: "mobile",
            });
          }
        }

        return router.push(salesRoutes.wybierak.get({ mode: "mobile" }));
      },
    },
  });

  const { context, matches } = current;

  const {
    groups,
    modemReturn,
    ftthProducts,
    mobileProducts,
    ftthOrchestrator,
    mobileOrchestrator,
    multiOrderOrchestrator,
    address,
    isAddressInFtthRange,
  } = context;
  const internet = ftthProducts?.internet;
  const mobileAccess = mobileProducts?.mobileAccess;
  const router = useRouter();
  const residentialAddress = address?.find((item) => {
    return item?.type === "RESIDENTIAL";
  })?.geographicalAddress;

  const ftthPhase = React.useMemo(() => {
    return getFtthOrchestratorPhase(
      ftthOrchestrator,
      ftthProducts,
      modemReturn,
    );
  }, [ftthOrchestrator, ftthProducts, modemReturn]);

  const mobilePhase = React.useMemo(() => {
    return getMobileOrchestratorPhase(
      mobileOrchestrator,
      multiOrderOrchestrator,
      mobileProducts,
    );
  }, [mobileOrchestrator, multiOrderOrchestrator, mobileProducts]);

  const onUpdateAddress = React.useCallback(() => {
    send({
      type: "UPDATE_ADDRESS",
    });
  }, [send]);

  const handleRedirectFtth = React.useCallback(() => {
    send({ type: "NEW_FTTH_ORDER" });
  }, [send]);

  const handleBlockFtth = React.useCallback(() => {
    modal.open();
  }, [modal]);

  const handleRedirectMobile = React.useCallback(() => {
    send({ type: "NEW_MULTI_ORDER" });
  }, [send]);

  if (
    matches("loading") ||
    matches("fetchClientAddress") ||
    matches("validateAddress")
  ) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <div className="flex justify-center items-center h-full">
          <CircleLoader />
        </div>
      </BlockContent>
    );
  }

  if (matches("paymentError")) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <GeneralErrorNotification
          callback={() => {
            return window.location.reload();
          }}
          copy="powrót"
          description="klient ma zaległości finansowe"
          title="brak możliwości sprzedaży usługi"
        />
      </BlockContent>
    );
  }

  if (matches("onAnotherAgentInProgress")) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <GeneralErrorNotification
          callback={() => {
            return window.location.reload();
          }}
          copy="powrót"
          description="spróbuj później"
          title="ktoś w tym samym czasie obsługuje tego klienta..."
        />
      </BlockContent>
    );
  }

  if (matches("onAnotherOrderInProgress")) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <GeneralErrorNotification
          callback={() => {
            return window.location.reload();
          }}
          copy="powrót"
          description="Klient jest w trakcie zamawiania usługi. Aby zamówić kolejną, musi dokończyć poprzedni zakup."
          icon="times-circle"
          title="nie wszystko na raz"
        />
      </BlockContent>
    );
  }

  if (matches("error")) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <GeneralErrorNotification
          callback={() => {
            return window.location.reload();
          }}
          copy="powrót"
          description="spróbuj później"
        />
      </BlockContent>
    );
  }

  return (
    <>
      {modal.isOpened && (
        <Modal
          onClose={modal.close}
          title="Złożenie zamówienia jest niemożliwe"
        >
          <BlockText className="mb-6" size="l">
            trwa odłączenie usługi nju światłowód
          </BlockText>
          <div className="max-w-[142px]">
            <Button
              display="inline"
              onPress={modal.close}
              size={{ "@onlyMobile": "s" }}
              variant="secondary"
            >
              powrót
            </Button>
          </div>
        </Modal>
      )}
      <OrchestratorsContext.Provider
        value={{
          ftth: ftthOrchestrator,
          mobile: mobileOrchestrator,
        }}
      >
        <div>
          <div className="bg-white" data-cy="client-order-info-box">
            <ClientOrderInfoBox
              address={residentialAddress}
              onUpdateAddress={onUpdateAddress}
              showAddress={true}
            />
          </div>
          {statusVariant === "banner" && (
            <>
              {ftthPhase === "phaseEmpty" && isAddressInFtthRange && (
                <Alert
                  iconName="exclamation-circle"
                  text="Klient w zasięgu nju światłowód - zaproponuj usługę i poinformuj o dodatkowym rabacie."
                />
              )}
              {ftthPhase === "phaseInProgress" && (
                <Alert
                  iconName="exclamation-circle"
                  text="W pakiecie taniej - poinformuj klienta, że pierwszy nju subskrypcja będzie tańszy o 3 zł miesięcznie, jeśli klient będzie miał aktywny nju światłowód. Rabat będzie naliczany co miesiąc w momencie płatności za nju subskrypcja. eSIM w nju - nowość! Od teraz Klient może mieć wirtualną kartę SIM. Zamówi ją za darmo przez apkę lub na koncie klienta na nju.pl."
                />
              )}
              <BlockContent grid="narrow" spacing="xl" tone="gray">
                <Separator size="m" tone="invisible" />
                <CustomerStatusBanner
                  address={address}
                  ftthPhase={ftthPhase}
                  groups={groups}
                  handleBlockFtth={handleBlockFtth}
                  handleRedirectFtth={handleRedirectFtth}
                  handleRedirectMobile={handleRedirectMobile}
                  internet={internet}
                  isAddressInFtthRange={!!isAddressInFtthRange}
                  mobileAccess={mobileAccess}
                  mobilePhase={mobilePhase}
                  onUpdateAddress={onUpdateAddress}
                />
              </BlockContent>
            </>
          )}
          {statusVariant === "details" && (
            <>
              <BlockContent grid="narrow" spacing="xl" tone="gray">
                <Separator size="m" tone="invisible" />
                <CustomerStatusDetails
                  groups={groups}
                  handleRedirectMobile={handleRedirectMobile}
                  mobile={mobileOrchestrator}
                  phase={mobilePhase}
                />
              </BlockContent>
            </>
          )}
        </div>
      </OrchestratorsContext.Provider>
    </>
  );
}
